import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ConnectionGuide } from "./ConnectionGuide";
import { useConvex, useQuery } from "convex/react";
import { api } from "@anthemreactv2/backend/labs-api";
import type { Id } from "@anthemreactv2/backend/convex/_generated/dataModel";
import type { CaptureFrame, PhidgetConfig } from "@anthemreactv2/contracts";
import { rateOfRise, temperature, type Interval } from "@anthemreactv2/roast-domain";
import type { LocalSession, RecorderStatus } from "@anthemreactv2/roast-recorder/types";
import { CloudGate, message } from "./cloud";
import { SharedAction, fieldNumber, fieldText } from "./SharedAction";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Metrics, Notice, PageHeading, Panel, SelectField } from "./components/workbench";
import { CurveChart } from "./CurveChart";
import {
  EVENT_NAMES,
  download,
  formatTime,
  type Reference,
  type Workspace,
} from "./workspace";

import { localRequest, readPairingFile } from "./recorder-client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
type Status = RecorderStatus;
export function LiveRecorder({
  reference,
  onOpen,
}: {
  reference: Reference | null;
  onOpen: (workspace: Workspace) => void;
}) {
  const [token, setToken] = useState<string | null>(null);
  const [pairing, setPairing] = useState(false);
  const [status, setStatus] = useState<Status | null>(null),
    [sessions, setSessions] = useState<LocalSession[]>([]),
    [selectedId, setSelectedId] = useState("");
  const [error, setError] = useState<string | null>(null),
    [reachable, setReachable] = useState(false),
    [frames, setFrames] = useState<CaptureFrame[]>([]);
  const [unit, setUnit] = useState<"F" | "C">("F"),
    [interval, setIntervalSeconds] = useState<Interval>(30),
    [windowSeconds, setWindow] = useState<Interval>(30);
  const cache = useRef(new Map<string, CaptureFrame[]>());
  const selected =
    status?.active ??
    sessions.find((session) => session.id === selectedId) ??
    sessions[0];
  const refresh = async () => {
    const next = await localRequest<Status>("status", token!);
    setStatus(next);
    setReachable(true);
    setSessions(await localRequest<LocalSession[]>("sessions", token!));
  };
  useEffect(() => {
    if (!token) return;
    let canceled = false,
      timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const next = await localRequest<Status>("status", token!);
        const rows = await localRequest<LocalSession[]>("sessions", token!);
        if (!canceled) {
          setStatus(next);
          setSessions(rows);
          setReachable(true);
        }
      } catch {
        if (!canceled) setReachable(false);
      }
      if (!canceled) timer = setTimeout(() => void tick(), 1000);
    };
    void tick();
    return () => {
      canceled = true;
      clearTimeout(timer);
    };
  }, [token]);
  useEffect(() => {
    if (!selected || !status || !reachable || !token) return;
    let canceled = false;
    const read = async () => {
      try {
        let rows = cache.current.get(selected.id) ?? [];
        while (rows.length < selected.nextSequence && !canceled) {
          const next = await localRequest<CaptureFrame[]>(
            `frames?id=${encodeURIComponent(selected.id)}&after=${rows.length - 1}`,
            token!,
          );
          if (!next.length) throw new Error("A local capture page is unavailable.");
          rows = [...rows, ...next];
          cache.current.set(selected.id, rows);
        }
        if (!canceled) setFrames(rows);
      } catch (cause) {
        if (!canceled) setError(message(cause));
      }
    };
    setFrames(cache.current.get(selected.id) ?? []);
    void read();
    return () => {
      canceled = true;
    };
  }, [selected?.id, selected?.nextSequence, token, reachable]);
  async function command(value: unknown) {
    if (!status || !reachable || !token)
      throw new Error("Reconnect to the recorder computer first.");
    await localRequest("command", token!, value);
    await refresh();
    setError(null);
  }
  const workspace: Workspace | null =
    selected && frames.length >= 2 && frames.some((frame) => frame.beanC !== null)
      ? {
          version: 2,
          source: {
            name: selected.name,
            provenance: "device",
            samples: frames,
          },
          reference,
          events: selected.events,
          history: [],
          playheadMs: selected.lastElapsedMs,
        }
      : null;
  const live = selected?.state === "recording";
  const bean = live
    ? reachable
      ? (status?.reading.beanC ?? null)
      : null
    : (frames.at(-1)?.beanC ?? null);
  const ror =
    workspace && (!live || (reachable && status?.reading.quality === "ok"))
      ? rateOfRise(frames, workspace.playheadMs, {
          unit,
          intervalSeconds: interval,
          windowSeconds,
          maxGapMs: 3000,
        })
      : null;
  const configured = status?.config,
    fresh = reachable && status?.reading.quality === "ok";
  return (
    <>
      <PageHeading
        title="Device connections"
        description="Capture on the roaster computer. Keep a durable local copy, then sync it with your team."
      />
      <ConnectionGuide />
      <Panel
        title="Pair this computer"
        description="The local connector keeps recording if the browser closes or your network drops."
      >
        <p className="text-sm text-muted-foreground">
          Start the Anthem recorder on the roaster computer, then choose its pairing file.
          Allow local network access if your browser asks. Pairing lasts until you leave
          this page; restarting the connector creates a new file.
        </p>
        <div className="max-w-lg space-y-2">
          <Label htmlFor="recorder-pairing">Recorder pairing file</Label>
          <Input
            id="recorder-pairing"
            type="file"
            accept=".json,application/json"
            disabled={pairing}
            onChange={async (event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file) return;
              setPairing(true);
              setError(null);
              try {
                const nextToken = await readPairingFile(file);
                const next = await localRequest<Status>("status", nextToken);
                cache.current.clear();
                setFrames([]);
                setSessions([]);
                setSelectedId("");
                setStatus(next);
                setReachable(true);
                setToken(nextToken);
              } catch (cause) {
                setError(message(cause));
              } finally {
                setPairing(false);
              }
            }}
          />
        </div>
        <p role="status" className="text-sm text-muted-foreground">
          {pairing
            ? "Pairing with the local recorder…"
            : token && reachable
              ? "Paired with this computer's recorder."
              : "Not paired with a reachable recorder."}
        </p>
      </Panel>
      {token && !reachable && (
        <Notice title="Recorder unavailable">
          Check the connector on this computer and your browser's local network
          permission. If the connector restarted, choose its new pairing file. Check any
          active capture before starting another roast.
        </Notice>
      )}
      {(error || status?.error) && <Notice error>{error ?? status?.error}</Notice>}
      <Panel
        title="Phidgets thermocouple recorder"
        description="First adapter: Phidget Network Server on this computer. Physical setup verification is still required."
        action={
          <Badge variant="outline">
            {fresh
              ? "Fresh bean reading"
              : configured
                ? "Waiting for bean reading"
                : "Not connected"}
          </Badge>
        }
      >
        <p className="text-sm text-muted-foreground">
          Confirm the serial number, channels, and probe type against your interface.
          Start the Phidget Network Server, then connect. Temperature capture runs
          independently of this browser.
        </p>
        {configured && (
          <p className="text-xs text-muted-foreground">
            Serial {configured.serial} · Hub port {configured.hubPort} · Bean{" "}
            {configured.beanChannel}
            {configured.environmentChannel !== null
              ? ` · Environment ${configured.environmentChannel}`
              : ""}{" "}
            · Type {configured.thermocouple}
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          <SharedAction
            label="Connect Phidgets"
            disabled={!reachable || Boolean(status?.active) || status?.connecting}
            description="Connect only to the local Phidget Network Server. This configures temperature channels; it does not operate the roaster's heat or airflow."
            fields={[
              {
                name: "serial",
                label: "Interface / hub serial number",
                type: "number",
                min: 1,
                value: configured?.serial.toString(),
              },
              {
                name: "hub",
                label: "Hub port (0 for a direct USB interface)",
                type: "number",
                min: 0,
                max: 15,
                value: configured?.hubPort.toString() ?? "0",
              },
              {
                name: "bean",
                label: "Bean temperature channel",
                type: "number",
                min: 0,
                max: 31,
                value: configured?.beanChannel.toString(),
              },
              {
                name: "environment",
                label: "Environment channel (optional)",
                type: "number",
                optional: true,
                min: 0,
                max: 31,
                value: configured?.environmentChannel?.toString(),
              },
              {
                name: "type",
                label: "Thermocouple type",
                options: ["K", "J", "E", "T"].map((value) => ({
                  value,
                  label: `Type ${value}`,
                })),
                value: configured?.thermocouple,
              },
              {
                name: "port",
                label: "Network Server port",
                type: "number",
                min: 1,
                max: 65535,
                value: configured?.port.toString() ?? "5661",
              },
            ]}
            submit={(data) =>
              command({
                action: "connect",
                config: {
                  serial: fieldNumber(data, "serial"),
                  hubPort: fieldNumber(data, "hub"),
                  beanChannel: fieldNumber(data, "bean"),
                  environmentChannel: fieldText(data, "environment")
                    ? fieldNumber(data, "environment")
                    : null,
                  thermocouple: fieldText(data, "type") as PhidgetConfig["thermocouple"],
                  port: fieldNumber(data, "port"),
                },
              })
            }
          />
          <SharedAction
            label="Start live roast"
            disabled={!fresh || Boolean(status?.active)}
            description="Begin capture at charge. This starts the roast clock; confirm stock consumption from the assigned task in Roastery operations."
            fields={[{ name: "name", label: "Roast name" }]}
            submit={(data, id) =>
              command({ action: "start", id, name: fieldText(data, "name") })
            }
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {fresh && status?.reading.beanC !== null
            ? `${temperature(status!.reading.beanC!, unit).toFixed(1)} °${unit} bean temperature`
            : "Live start stays unavailable until the bean probe supplies a valid, fresh temperature."}
        </p>
      </Panel>
      {selected && (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SelectField
              label="Local captures (latest 100)"
              value={selected.id}
              disabled={Boolean(status?.active)}
              onChange={(event) => setSelectedId(event.target.value)}
            >
              {sessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.name} · {session.state}
                </option>
              ))}
            </SelectField>
            <Badge variant="outline">
              {selected.state === "interrupted"
                ? "Interrupted · local copy preserved"
                : live
                  ? "Recording to disk"
                  : "Complete · local copy preserved"}
            </Badge>
          </div>
          <Metrics
            items={[
              {
                label: "Elapsed since charge",
                value: formatTime(
                  selected.lastElapsedMs < 0 ? 0 : selected.lastElapsedMs,
                ),
                note: live ? "Recorder clock" : "Last recorded time",
              },
              {
                label: "Bean temperature",
                value: bean === null ? "—" : temperature(bean, unit).toFixed(1),
                unit: `°${unit}`,
                note: live && !fresh ? "No fresh reading" : "Measured bean channel",
              },
              {
                label: "Rate of Rise",
                value: ror === null ? "—" : ror.toFixed(1),
                unit: `°${unit} / ${interval}s`,
                note: `${windowSeconds}s calculation window`,
              },
            ]}
          />
          <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_288px]">
            <Panel
              title={selected.name}
              description={
                reference
                  ? `Overlay: ${reference.name} · ${reference.provenance}`
                  : "Live curve · choose a reference from Roasts & profiles"
              }
            >
              {workspace ? (
                <CurveChart
                  workspace={workspace}
                  unit={unit}
                  interval={interval}
                  windowSeconds={windowSeconds}
                />
              ) : (
                <p className="py-16 text-center text-sm text-muted-foreground">
                  Waiting for enough recorded samples to draw the curve…
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-3">
                <SelectField
                  label="Temperature"
                  value={unit}
                  onChange={(event) => setUnit(event.target.value as "F" | "C")}
                >
                  <option value="F">Fahrenheit</option>
                  <option value="C">Celsius</option>
                </SelectField>
                <SelectField
                  label="RoR display"
                  value={interval}
                  onChange={(event) =>
                    setIntervalSeconds(Number(event.target.value) as Interval)
                  }
                >
                  {[15, 30, 60].map((value) => (
                    <option key={value} value={value}>
                      °{unit} / {value}s
                    </option>
                  ))}
                </SelectField>
                <SelectField
                  label="Calculation window"
                  value={windowSeconds}
                  onChange={(event) => setWindow(Number(event.target.value) as Interval)}
                >
                  {[15, 30, 60].map((value) => (
                    <option key={value} value={value}>
                      {value}s
                    </option>
                  ))}
                </SelectField>
              </div>
            </Panel>
            <Panel
              title="Roast events"
              description="Marks use the recorder's saved elapsed time."
            >
              {EVENT_NAMES.filter(
                (name) =>
                  !["Heat adjustment", "Airflow adjustment", "Note"].includes(name),
              ).map((name) => {
                const event = selected.events.find((row) => row.name === name);
                return (
                  <Button
                    key={name}
                    variant="ghost"
                    className="flex w-full justify-between"
                    disabled={!live || !reachable || Boolean(event)}
                    onClick={() =>
                      void command({
                        action: "event",
                        id: selected.id,
                        event: {
                          id: crypto.randomUUID(),
                          name,
                          elapsedMs: selected.lastElapsedMs,
                          note: "",
                        },
                      }).catch((cause) => setError(message(cause)))
                    }
                  >
                    {name}
                    <span className="tabular-nums">
                      {event ? formatTime(event.elapsedMs) : "+"}
                    </span>
                  </Button>
                );
              })}
              <SharedAction
                label="Add observation"
                disabled={!live || !reachable}
                description="Log an observation or a manual heat / airflow adjustment at the latest recorded time."
                fields={[
                  {
                    name: "type",
                    label: "Event type",
                    options: ["Heat adjustment", "Airflow adjustment", "Note"].map(
                      (value) => ({ value, label: value }),
                    ),
                  },
                  { name: "note", label: "Observation" },
                ]}
                submit={(data, id) =>
                  command({
                    action: "event",
                    id: selected.id,
                    event: {
                      id,
                      name: fieldText(data, "type"),
                      elapsedMs: selected.lastElapsedMs,
                      note: fieldText(data, "note"),
                    },
                  })
                }
              />
              {selected.state !== "complete" && (
                <SharedAction
                  label="Finish recording"
                  disabled={!reachable || selected.nextSequence < 2}
                  description="Close this capture and preserve its frames and event marks. Record a Drop event separately when coffee leaves the drum."
                  submit={() => command({ action: "finish", id: selected.id })}
                />
              )}
            </Panel>
          </div>
          {selected.state === "interrupted" && (
            <Panel
              title="Recover interrupted capture"
              description="A recorder restart interrupts its monotonic clock. Enter the measured time since charge to resume with a visible gap."
            >
              <SharedAction
                label="Resume capture"
                disabled={!fresh}
                description="Reconnect the exact same interface and channels first. Confirm the current elapsed time from charge; no readings are invented for the interruption."
                fields={[
                  {
                    name: "elapsed",
                    label: "Current elapsed time since charge (seconds)",
                    type: "number",
                    min: Math.ceil((selected.lastElapsedMs + 2) / 1000),
                  },
                ]}
                submit={(data) =>
                  command({
                    action: "resume",
                    id: selected.id,
                    elapsedMs: Math.round(fieldNumber(data, "elapsed") * 1000),
                  })
                }
              />
            </Panel>
          )}
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              disabled={
                !workspace ||
                selected.state !== "complete" ||
                frames.length !== selected.nextSequence
              }
              onClick={() => workspace && onOpen({ ...workspace, playheadMs: 0 })}
            >
              Open recorded roast
            </Button>
            <Button
              variant="outline"
              disabled={frames.length !== selected.nextSequence}
              onClick={() =>
                download(
                  JSON.stringify({ version: 1, session: selected, frames }),
                  "anthem-device-capture.json",
                  "application/json",
                )
              }
            >
              Export capture journal
            </Button>
          </div>
          <Panel
            title="Share this capture"
            description="Sync recorded chunks to your roastery. Local capture continues if the browser or network closes."
          >
            <CloudGate>
              <SyncCapture
                key={selected.id}
                session={selected}
                token={token ?? ""}
                reachable={reachable}
              />
            </CloudGate>
          </Panel>
        </>
      )}
    </>
  );
}
function SyncCapture({
  session,
  token,
  reachable,
}: {
  session: LocalSession;
  token: string;
  reachable: boolean;
}) {
  const client = useConvex(),
    mine = useQuery(api.labs.roasteries.mine, {});
  const [roasteryId, setRoastery] = useState(""),
    [machineId, setMachine] = useState("");
  const selectedRoastery =
    mine?.roasteries.find((row) => row._id === roasteryId) ?? mine?.roasteries[0];
  const machines = useQuery(
    api.labs.roasteries.machines,
    selectedRoastery ? { roasteryId: selectedRoastery._id } : "skip",
  );
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null),
    [result, setResult] = useState<string | null>(null);
  const flight = useRef(false);
  const [auto, setAuto] = useState(false);
  const syncRef = useRef<() => Promise<void>>(async () => {});
  useEffect(() => {
    if (!auto) return;
    const timer = setInterval(() => {
      void syncRef.current();
    }, 10000);
    return () => clearInterval(timer);
  }, [auto]);
  const canUpload =
    selectedRoastery && ["owner", "manager", "operator"].includes(selectedRoastery.role);
  syncRef.current = async () => {
    if (
      flight.current ||
      !reachable ||
      !canUpload ||
      !machineId ||
      !session.nextSequence ||
      !selectedRoastery
    )
      return;
    flight.current = true;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const roasteryId = selectedRoastery._id;
      const recordingId = await client.mutation(api.labs.recordings.begin, {
        roasteryId,
        machineId: machineId as Id<"labsMachines">,
        clientSessionId: session.id,
        name: session.name,
        provenance: "device",
        deviceDescription: `Phidgets ${session.config.serial}; hub ${session.config.hubPort}; bean ${session.config.beanChannel}; environment ${session.config.environmentChannel ?? "none"}; type ${session.config.thermocouple}`,
      });
      const recording = await client.query(api.labs.recordings.get, {
        roasteryId,
        recordingId,
      });
      let next = recording.nextSequence,
        chunk = recording.nextChunk;
      while (next < session.nextSequence) {
        const frames = (
          await localRequest<CaptureFrame[]>(
            `frames?id=${encodeURIComponent(session.id)}&after=${next - 1}`,
            token,
          )
        ).filter((frame) => frame.sequence < session.nextSequence);
        if (!frames.length)
          throw new Error(
            "A local frame is unavailable. The shared recording remains unsealed.",
          );
        await client.mutation(api.labs.recordings.append, {
          roasteryId,
          recordingId,
          chunk,
          json: JSON.stringify({ version: 1, frames }),
        });
        next += frames.length;
        chunk++;
      }
      if (session.state === "complete") {
        await client.mutation(api.labs.recordings.seal, {
          roasteryId,
          recordingId,
          expectedFrames: session.nextSequence,
          events: session.events,
        });
        setAuto(false);
      }
      setResult(
        session.state === "complete"
          ? "Complete capture shared. Your local copy is preserved."
          : `Synced ${next} frames. Sync again after finishing to include all frames and event marks.`,
      );
    } catch (cause) {
      setError(message(cause));
      setAuto(false);
    } finally {
      flight.current = false;
      setBusy(false);
    }
  };
  if (!mine) return <p role="status">Loading roasteries…</p>;
  if (!selectedRoastery)
    return (
      <p className="text-sm text-muted-foreground">
        Create or join a{" "}
        <Link to="/roasting/operations" className="underline">
          shared roastery
        </Link>{" "}
        first.
      </p>
    );
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          label="Roastery"
          value={selectedRoastery._id}
          disabled={busy || auto}
          onChange={(event) => {
            setRoastery(event.target.value);
            setMachine("");
            setResult(null);
          }}
        >
          {mine.roasteries.map((row) => (
            <option key={row._id} value={row._id}>
              {row.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Captured on machine"
          value={machineId}
          disabled={busy || auto}
          onChange={(event) => {
            setMachine(event.target.value);
            setResult(null);
          }}
        >
          <option value="">Choose a machine…</option>
          {machines?.map((row) => (
            <option key={row._id} value={row._id}>
              {row.name}
            </option>
          ))}
        </SelectField>
      </div>
      <Button
        disabled={busy || !reachable || !canUpload || !machineId || !session.nextSequence}
        onClick={() => void syncRef.current()}
      >
        {busy ? "Syncing capture…" : "Sync capture"}
      </Button>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1 accent-primary"
          checked={auto}
          disabled={!canUpload || !machineId || session.state === "complete"}
          onChange={(event) => setAuto(event.target.checked)}
        />
        Keep syncing every 10 seconds while this page is open
      </label>
      <p className="text-xs text-muted-foreground">
        Closing the page pauses cloud sync. The recorder keeps saving on disk; reopening
        and syncing resumes from the last acknowledged frame.
      </p>
      {!canUpload && (
        <p className="text-sm text-muted-foreground">
          An operator or manager role is required to upload recordings.
        </p>
      )}
      {error && <Notice error>{error}</Notice>}
      {result && (
        <p role="status" className="text-sm text-muted-foreground">
          {result}
        </p>
      )}
    </div>
  );
}
